/* eslint-env browser */

// Máscaras vanilla dos formulários públicos (landing + onboarding).
// Bind por atributo: data-mask="phone-br" | "phone-us" | "cpf-cnpj" |
// "ein-us" | "postal-br" | "postal-us" | "money-brl". Sem alvos, o init é
// um no-op. Identificadores, telefones e códigos postais são normalizados para
// dígitos no submit; o campo monetário espelha centavos num hidden input
// (data-money-hidden="<id>") — o servidor nunca recebe texto formatado.

export function onlyDigits(value, maxLength) {
  const digits = String(value ?? "").replace(/\D/g, "");
  return maxLength ? digits.slice(0, maxLength) : digits;
}

function nationalPhoneDigits(value, callingCode, maxLength) {
  const raw = String(value ?? "").trim();
  const digits = onlyDigits(raw);
  let internationalDigits = null;

  if (raw.startsWith("+")) internationalDigits = digits;
  if (raw.startsWith("00")) internationalDigits = digits.slice(2);
  if (!internationalDigits && digits.length > maxLength && digits.startsWith(callingCode)) {
    internationalDigits = digits;
  }

  const national = internationalDigits?.startsWith(callingCode)
    ? internationalDigits.slice(callingCode.length)
    : digits;
  // Preserve one excess digit so malformed input stays visibly invalid instead
  // of being truncated into a different, apparently valid phone number.
  return national.slice(0, maxLength + 1);
}

// Padrão brasileiro dinâmico: assinante iniciado em 9 mantém a apresentação
// móvel (XX) XXXXX-XXXX mesmo enquanto incompleto; os demais usam fixo.
export function formatPhoneBr(value) {
  const d = nationalPhoneDigits(value, "55", 11);
  if (!d) return "";
  if (d.length <= 2) return `(${d}`;
  const ddd = d.slice(0, 2);
  const rest = d.slice(2);
  if (rest.startsWith("9")) {
    if (rest.length <= 5) return `(${ddd}) ${rest}`;
    return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
  }
  if (rest.length <= 4) return `(${ddd}) ${rest}`;
  return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
}

// Padrão NANP usado no onboarding US: (AAA) BBB-CCCC.
export function formatPhoneUs(value) {
  const d = nationalPhoneDigits(value, "1", 10);
  if (!d) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// Employer Identification Number: 00-0000000.
export function formatEinUs(value) {
  const d = onlyDigits(value, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}-${d.slice(2)}`;
}

export function formatPostalBr(value) {
  const d = onlyDigits(value, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

export function formatPostalUs(value) {
  const d = onlyDigits(value, 9);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

// CPF até 11 dígitos (000.000.000-00); do 12º em diante CNPJ
// (00.000.000/0000-00), limitado a 14 dígitos.
export function formatCpfCnpj(value) {
  const d = onlyDigits(value, 14);
  if (!d) return "";
  if (d.length <= 11) {
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
  const tail = d.slice(12);
  const base = `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}`;
  return tail ? `${base}-${tail}` : base;
}

// Formatação monetária direita→esquerda: dígitos são centavos.
export function formatMoneyBrl(value, { locale = "pt-BR", currency = "BRL" } = {}) {
  const cents = onlyDigits(value, 15);
  if (!cents) return "";
  const formatter = new globalThis.Intl.NumberFormat(locale, { style: "currency", currency });
  return formatter.format(Number(cents) / 100);
}

const NORMALIZE_ON_SUBMIT = new Set([
  "phone-br", "phone-us", "cpf-cnpj", "ein-us", "postal-br", "postal-us",
]);

function ensureAttributes(input, kind) {
  const defaults = {
    "phone-br": {
      inputmode: "tel",
      autocomplete: "tel",
      maxlength: "15",
    },
    "phone-us": {
      inputmode: "tel",
      autocomplete: "tel",
      maxlength: "14",
    },
    "cpf-cnpj": { inputmode: "numeric", autocomplete: "off", maxlength: "18" },
    "ein-us": { inputmode: "numeric", autocomplete: "off", maxlength: "10" },
    "postal-br": { inputmode: "numeric", autocomplete: "postal-code", maxlength: "9" },
    "postal-us": { inputmode: "numeric", autocomplete: "postal-code", maxlength: "10" },
    "money-brl": { inputmode: "numeric", autocomplete: "off", maxlength: "22" },
  }[kind];
  if (!defaults) return;
  Object.entries(defaults).forEach(([name, fallback]) => {
    if (!input.getAttribute(name)) input.setAttribute(name, fallback);
  });
}

function formatFor(kind, value, options) {
  if (kind === "phone-br") return formatPhoneBr(value);
  if (kind === "phone-us") return formatPhoneUs(value);
  if (kind === "cpf-cnpj") return formatCpfCnpj(value);
  if (kind === "ein-us") return formatEinUs(value);
  if (kind === "postal-br") return formatPostalBr(value);
  if (kind === "postal-us") return formatPostalUs(value);
  if (kind === "money-brl") return formatMoneyBrl(value, options);
  return String(value ?? "");
}

function moneyHiddenFor(input, root) {
  const id = input.getAttribute("data-money-hidden");
  return id ? root.getElementById(id) : null;
}

function bindInput(input, root) {
  const kind = input.getAttribute("data-mask");
  const options = {
    locale: input.getAttribute("data-mask-locale") || "pt-BR",
    currency: input.getAttribute("data-mask-currency") || "BRL",
  };
  ensureAttributes(input, kind);

  const hidden = kind === "money-brl" ? moneyHiddenFor(input, root) : null;
  const apply = () => {
    input.value = formatFor(kind, input.value, options);
    if (hidden) hidden.value = onlyDigits(input.value, 15);
  };

  if (hidden && hidden.value) input.value = hidden.value;
  if (input.value) apply();
  input.addEventListener("input", apply);
  input.addEventListener("blur", () => {
    if (["phone-br", "phone-us"].includes(kind) && input.value === "(") input.value = "";
  });

  const form = input.closest("form");
  if (form && NORMALIZE_ON_SUBMIT.has(kind) && !form.dataset.rilMaskSubmit) {
    form.dataset.rilMaskSubmit = "true";
    // Envio async: o layout monta `new FormData(form)` dentro do próprio
    // handler de submit; o evento "formdata" é o único hook que ainda
    // consegue trocar o payload por dígitos nesse caminho.
    form.addEventListener("formdata", (event) => {
      form.querySelectorAll("[data-mask]").forEach((el) => {
        if (!el.name || !NORMALIZE_ON_SUBMIT.has(el.getAttribute("data-mask"))) return;
        event.formData.set(el.name, onlyDigits(el.value));
      });
    });
    // Submit nativo (fluxos sem fetch, ex.: onboarding): normaliza os campos
    // antes da serialização e re-mascara em seguida caso a página permaneça.
    form.addEventListener("submit", () => {
      const masked = [];
      form.querySelectorAll("[data-mask]").forEach((el) => {
        if (!NORMALIZE_ON_SUBMIT.has(el.getAttribute("data-mask"))) return;
        masked.push([el, el.value]);
        el.value = onlyDigits(el.value);
      });
      globalThis.setTimeout(() => {
        masked.forEach(([el, value]) => { el.value = value; });
      }, 0);
    }, true);
  }
}

function maskValueIsComplete(input) {
  const kind = input.getAttribute("data-mask");
  if (!kind || kind === "money-brl") return true;
  const digits = onlyDigits(input.value);
  if (!digits) return !input.required;

  if (kind === "phone-br") {
    const ddd = digits.slice(0, 2);
    const subscriber = digits.slice(2);
    const validDdd = /^[1-9][0-9]$/.test(ddd);
    const validLandline = digits.length === 10 && /^[2-5][0-9]{7}$/.test(subscriber);
    const validMobile = digits.length === 11 && /^9[0-9]{8}$/.test(subscriber);
    return validDdd && (validLandline || validMobile);
  }
  if (kind === "phone-us") {
    return digits.length === 10 && /^[2-9][0-9]{2}[2-9][0-9]{6}$/.test(digits);
  }
  if (kind === "ein-us") return digits.length === 9;
  if (kind === "postal-br") return digits.length === 8;
  if (kind === "postal-us") return [5, 9].includes(digits.length);
  if (kind === "cpf-cnpj") {
    const expected = Number.parseInt(input.dataset.maskRequiredDigits, 10);
    return Number.isFinite(expected) ? digits.length === expected : [11, 14].includes(digits.length);
  }
  return true;
}

function clearValidationState(input) {
  delete input.dataset.validationState;
  input.removeAttribute("aria-invalid");
}

function dismissServerValidationError(input) {
  const errorId = input.dataset.validationErrorId;
  if (!errorId) return;

  const error = input.ownerDocument.getElementById(errorId);
  if (error) error.hidden = true;

  const describedBy = (input.getAttribute("aria-describedby") || "")
    .split(/\s+/)
    .filter((id) => id && id !== errorId);
  if (describedBy.length > 0) input.setAttribute("aria-describedby", describedBy.join(" "));
  else input.removeAttribute("aria-describedby");

  delete input.dataset.validationServerInvalid;
  input.dataset.validationPendingServer = "true";
  clearValidationState(input);
}

function clientValueIsValid(input) {
  const matches = input.dataset.validationMatches;
  const comparison = matches ? input.form?.elements.namedItem(matches) : null;
  const matchesValue = !comparison || input.value === comparison.value;
  return input.validity.valid && maskValueIsComplete(input) && matchesValue;
}

function updateValidationState(input, { touched = false } = {}) {
  if (touched) input.dataset.validationTouched = "true";
  if (input.dataset.validationTouched !== "true") return;

  const emptyOptional = !input.required && String(input.value ?? "").trim() === "";
  if (emptyOptional) {
    if (input.dataset.validationServerInvalid === "true") dismissServerValidationError(input);
    else clearValidationState(input);
    return;
  }

  const valid = clientValueIsValid(input);
  if (input.dataset.validationServerInvalid === "true") {
    if (valid) dismissServerValidationError(input);
    return;
  }
  if (input.dataset.validationPendingServer === "true" && valid) {
    clearValidationState(input);
    return;
  }

  input.dataset.validationState = valid ? "valid" : "invalid";
  if (valid) input.removeAttribute("aria-invalid");
  else input.setAttribute("aria-invalid", "true");
}

function bindFormValidation(form) {
  if (form.dataset.rilValidationBound === "true") return;
  form.dataset.rilValidationBound = "true";

  const fields = [...form.querySelectorAll(".ril-input")].filter((input) => (
    !input.disabled && !input.readOnly && input.type !== "hidden"
  ));

  fields.forEach((input) => {
    if (input.getAttribute("aria-invalid") === "true" || input.dataset.validationState === "invalid") {
      input.dataset.validationTouched = "true";
      input.dataset.validationState = "invalid";
    }
    input.addEventListener("blur", () => updateValidationState(input, { touched: true }));
    input.addEventListener("input", () => {
      updateValidationState(input);
      fields
        .filter((candidate) => candidate.dataset.validationMatches === input.name)
        .forEach((candidate) => updateValidationState(candidate));
    });
    input.addEventListener("change", () => updateValidationState(input, { touched: true }));
  });

  form.addEventListener("invalid", (event) => {
    if (fields.includes(event.target)) updateValidationState(event.target, { touched: true });
  }, true);
  form.querySelector('[aria-invalid="true"]')?.focus();
}

export function initLandingForms(root = document) {
  root.querySelectorAll("input[data-mask]").forEach((input) => bindInput(input, root));
  root.querySelectorAll("form").forEach((form) => bindFormValidation(form));
  root.querySelectorAll("[data-ril-lead-form], [data-ril-demo-form]").forEach((form) => {
    if (form.dataset.demoSubmitBound) return;
    form.dataset.demoSubmitBound = "true";
    const buttons = [...form.querySelectorAll('[type="submit"]')];
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!form.checkValidity()) { form.reportValidity(); return; }
      if (form.dataset.demoSubmitting === "true") return;
      form.dataset.demoSubmitting = "true";
      buttons.forEach((button) => { button.disabled = true; });
      const feedback = form.querySelector("[data-ril-lead-feedback], [data-ril-demo-feedback]");
      const key = form.elements.namedItem("submission_key");
      try {
        const response = await globalThis.fetch(form.action, {
          method: "POST", credentials: "same-origin",
          headers: { Accept: "application/json", "X-CSRF-Token": root.querySelector('meta[name="csrf-token"]')?.content || "" },
          body: new FormData(form),
        });
        const result = await response.json();
        if (response.ok && result.ok === true && result.receipt_key === key?.value) {
          form.reset();
          key.value = globalThis.crypto.randomUUID();
          form.elements.namedItem("rendered_at").value = Math.floor(Date.now() / 1000);
          if (feedback) { feedback.setAttribute("role", "status"); feedback.textContent = form.dataset.success; }
        } else if (feedback) {
          feedback.setAttribute("role", "alert");
          feedback.textContent = result.errors?.join(" · ") || form.dataset.error;
        }
      } catch {
        // Keep the same key and fields: an accepted POST with a lost response is replayable.
        if (feedback) { feedback.setAttribute("role", "alert"); feedback.textContent = form.dataset.error; }
      } finally {
        if (feedback) feedback.hidden = false;
        delete form.dataset.demoSubmitting;
        buttons.forEach((button) => { button.disabled = false; });
      }
    });
    globalThis.addEventListener("pageshow", () => {
      delete form.dataset.demoSubmitting;
      buttons.forEach((button) => { button.disabled = false; });
    });
  });
}
